import { either } from 'fp-ts';
import { observer } from 'mobx-react-lite';
import { IModelPredictionStoreMST } from 'modules/Predictions/mst/ModelPredictionStore';
import { IWorkflowPredictionStoreMST } from 'modules/Predictions/mst/WorkflowPredictionStore';
import { TSupportedPredictionCategories, predictionCategoryIsUsable, predictionCategoryToInputType } from 'modules/Predictions/types';
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { IconPlus, IconUpload, IconPublicPreview, IconBatchPredict, IconEdit04 } from 'components/Icons';
import { css, cx } from '@linaria/core';
import { useDebouncedValue } from 'utils/hooks/useDebouncedValue';
import { Button } from 'components/ui_library/Button';
import { useIsOwner } from 'components/NoAppState/NoAppState.hooks';
import { Popover, PopoverAction, PopoverGroup, usePopoverState } from 'components/Popover';
import { usePresetInputsUploader } from 'components/PresetInputsUploader/usePresetInputsUploader';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { AddInput } from './AddInput';
import { InputActionButton, InputActionTooltip } from './PredictionSidebar.styles';
import { useUploadModal } from './useUploadModal';
import { useBatchPredictModal } from './BatchPredictModal/useBatchPredictModal';
const useUploadAction = (modelTypeForUi: TSupportedPredictionCategories, store: IModelPredictionStoreMST | IWorkflowPredictionStoreMST, pageName: string, isLoggedIn: boolean) => {
  const {
    asPath,
    push
  } = useRouter();
  const [mediaType, text] = useMemo<[CF.UI.UISupportedUploadTypes, string]>(() => {
    if ([TSupportedPredictionCategories.Text, TSupportedPredictionCategories.TextJson].includes(modelTypeForUi)) {
      return ['text', 'Try your own text'];
    } else if ([TSupportedPredictionCategories.Image, TSupportedPredictionCategories.ImageJson].includes(modelTypeForUi)) {
      return ['image,video', 'Try your own image or video'];
    } else if ([TSupportedPredictionCategories.Audio, TSupportedPredictionCategories.AudioJson].includes(modelTypeForUi)) {
      return ['audio', 'Try your own audio'];
    }
    return ['image', ''];
  }, [modelTypeForUi]);
  const {
    open
  } = useUploadModal({
    title: text,
    store,
    mediaType,
    pageName
  });
  const redirectToLogin = useCallback(() => {
    push({
      pathname: '/login',
      query: {
        from: asPath
      }
    });
  }, [asPath]);
  if (!isLoggedIn) {
    const text_ = either.isLeft(store.publicList) ? 'Log In To Predict' : 'Log In To Use Model';
    return {
      text: text_,
      onClick: redirectToLogin
    };
  }
  return {
    text,
    onClick: open
  };
};
export const UploadAction = observer(({
  isLoggedIn,
  store,
  editableEntity,
  modelTypeForUi,
  trainedVersionsExist,
  pageName
}: {
  store: IModelPredictionStoreMST | IWorkflowPredictionStoreMST;
  editableEntity: CF.UI.EditableEntity;
  isLoggedIn: boolean;
  modelTypeForUi: TSupportedPredictionCategories;
  trainedVersionsExist: boolean;
  pageName: string;
}): JSX.Element | null => {
  const [mouseOver, setMouseOver] = useState(false);
  const debouncedMouseOver = useDebouncedValue(mouseOver, 140);
  const mouseEnter = useCallback(() => {
    setMouseOver(true);
  }, []);
  const mouseLeave = useCallback(() => {
    setMouseOver(false);
  }, []);
  const helpPopover = usePopoverState({
    placement: 'bottom-end'
  });
  const {
    batchPredictModal
  } = useFeatureFlagsLD();
  const isBatchPredictModalEnabled = typeof batchPredictModal === 'boolean' ? batchPredictModal : true;
  const {
    text,
    onClick
  } = useUploadAction(modelTypeForUi, store, pageName, isLoggedIn);
  const {
    onClick: textModalClick
  } = useUploadAction(TSupportedPredictionCategories.Text, store, pageName, isLoggedIn);
  const {
    open
  } = useBatchPredictModal({});
  const isOwner = useIsOwner();
  if (modelTypeForUi === 'waiting') return null;
  const inputType = predictionCategoryToInputType(modelTypeForUi);
  if (!inputType) return null;
  const openModal = usePresetInputsUploader(editableEntity, inputType, store);
  const isAddPublicPreviewVisible = store.hasTrained && editableEntity.isEditable && predictionCategoryIsUsable(modelTypeForUi);
  if (!trainedVersionsExist) {
    return null;
  }
  const renderTryYourOwnOption = (modalInputType: string) => <PopoverGroup className={popoverGroupStyle}>
        <Button variant="unstyled" onClick={() => modalInputType === 'text' ? textModalClick() : onClick()}>
          <div className={optionTextStyle}>
            {modalInputType === 'text' ? <IconEdit04 size={20} color="#344054" /> : <IconUpload size={18} color="#344054" />}
            {modalInputType === 'image' && <div>Try your own image or video</div>}
            {modalInputType === 'text' && <div>Try your own text</div>}
            {!['image', 'text'].includes(modalInputType) && <div>{text}</div>}
          </div>
        </Button>
      </PopoverGroup>;
  return <>
        <PopoverAction popover={helpPopover}>
          <AddInput className={InputActionButton} data-testid="upload-action" onMouseEnter={mouseEnter} onMouseLeave={mouseLeave}>
            <IconPlus size={20} />

            {(mouseOver || debouncedMouseOver) && text && <div data-testid="upload-action-tooltip" className={cx(InputActionTooltip, 'primary', mouseOver ? 'entering' : 'exiting')}>
                <p>{text}</p>
              </div>}
          </AddInput>
        </PopoverAction>

        <Popover aria-label="Help" popover={helpPopover} className={popoverStyle}>
          {inputType === 'any' ? <>
              {renderTryYourOwnOption('image')}
              {renderTryYourOwnOption('text')}
            </> : <>{renderTryYourOwnOption(inputType)}</>}

          {isOwner && isAddPublicPreviewVisible && <PopoverGroup className={popoverGroupStyle}>
              <Button variant="unstyled" onClick={openModal}>
                <div className={optionTextStyle}>
                  <IconPublicPreview size={20} color="#344054" />
                  <div>Add Public Preview Examples</div>
                </div>
              </Button>
            </PopoverGroup>}
          {isBatchPredictModalEnabled && <PopoverGroup onClick={open} className={popoverGroupStyle}>
              <Button variant="unstyled">
                <div className={optionTextStyle}>
                  <IconBatchPredict size={20} color="#344054" />
                  <div>Batch predict on app inputs</div>
                </div>
              </Button>
            </PopoverGroup>}
        </Popover>
      </>;
});
const optionTextStyle = "odules__Predictions__components__PredictionSidebar__UploadAction_tsx-optionTextStyle-oi62fvm";
const popoverStyle = "odules__Predictions__components__PredictionSidebar__UploadAction_tsx-popoverStyle-p1ggop9y";
const popoverGroupStyle = "odules__Predictions__components__PredictionSidebar__UploadAction_tsx-popoverGroupStyle-ptpb8am";

require("./UploadAction.linaria.module.css!=!../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./UploadAction.tsx");